import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Icons } from '../common';

const QCDashboard = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [statsData, setStatsData] = useState({ passRate: 0, criticalIssues: 0, avgScore: 0 });
  const [recentAnalyses, setRecentAnalyses] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/qc/analyses', { params: { limit: 1000 } });
        const { total, analyses } = response.data;

        const passedCount = analyses.filter(a => a.overallStatus === 'PASSED').length;
        const passRate = analyses.length > 0 ? Math.round((passedCount / analyses.length) * 100) : 0;
        const criticalIssues = analyses.reduce((sum, a) => sum + (a.criticalIssuesCount || 0), 0);
        const avgScore = analyses.length > 0
          ? (analyses.reduce((sum, a) => sum + (a.overallScore || 0), 0) / analyses.length).toFixed(1)
          : '0';

        setTotalCount(total);
        setStatsData({ passRate, criticalIssues, avgScore });
        setRecentAnalyses(analyses.slice(0, 5));
      } catch (err) {
        console.error('Failed to load QC dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const stats = [
    { label: 'Files Analyzed', value: loading ? '—' : String(totalCount), color: 'from-blue-500 to-cyan-500' },
    { label: 'Pass Rate', value: loading ? '—' : `${statsData.passRate}%`, color: 'from-green-500 to-emerald-500' },
    { label: 'Critical Issues', value: loading ? '—' : String(statsData.criticalIssues), color: 'from-red-500 to-rose-500' },
    { label: 'Avg Score', value: loading ? '—' : String(statsData.avgScore), color: 'from-purple-500 to-pink-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Icons.CheckCircleIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quality Control</h1>
              <p className="text-sm text-gray-600">Validate articles before production</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* Animated background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

              {/* Animated border */}
              <div className="absolute inset-0 rounded-2xl border border-gray-200/50 group-hover:border-indigo-200/50 transition-colors duration-300" />

              <div className="relative">
                <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                  {stat.value}
                </p>
                <div className={`h-1 w-12 bg-gradient-to-r ${stat.color} rounded-full`} />
              </div>
            </div>
          ))}
        </div>

        {/* Primary Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">

          {/* Upload Card */}
          <div
            onMouseEnter={() => setHoveredCard('upload')}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => navigate('/qc/upload')}
            className="group relative cursor-pointer h-64 rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-2xl transition-all duration-500"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-blue-50" />

            {/* Floating shapes */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-indigo-400/20 to-blue-400/20 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700" />

            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
              <div className={`mb-6 p-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 shadow-lg transform transition-all duration-500 ${hoveredCard === 'upload' ? 'scale-110 rotate-12' : 'scale-100'}`}>
                <Icons.DocumentIcon className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">Upload New File</h3>
              <p className="text-gray-600 mb-6">Drag and drop your Word document or click to browse</p>

              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                <Icons.UploadIcon className="w-5 h-5" />
                Upload Document
              </button>

              {/* Progress indicator */}
              <div className="mt-6 w-full max-w-xs h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full animate-pulse" style={{width: '0%'}} />
              </div>
            </div>
          </div>

          {/* View Articles Card */}
          <div
            onMouseEnter={() => setHoveredCard('articles')}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => navigate('/qc/articles')}
            className="group relative cursor-pointer h-64 rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-2xl transition-all duration-500"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-teal-50" />

            {/* Floating shapes */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700" />

            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
              <div className={`mb-6 p-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg transform transition-all duration-500 ${hoveredCard === 'articles' ? 'scale-110 -rotate-12' : 'scale-100'}`}>
                <Icons.DocumentListIcon className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">View Completed Articles</h3>
              <p className="text-gray-600 mb-6">Search and review previously analyzed documents</p>

              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                <Icons.SearchIcon className="w-5 h-5" />
                Browse Articles
              </button>

              {/* Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-300/50 rounded-full text-sm font-medium text-emerald-700">
                {loading ? '…' : `${totalCount} analyzed`}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Analyses */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200/50">
          <div className="p-8 border-b border-gray-200/50">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-indigo-600 to-blue-600 rounded-full" />
              Recent Analyses
            </h2>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            ) : recentAnalyses.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <p className="text-lg font-medium">No analyses yet</p>
                <p className="text-sm mt-1">Upload a document to get started</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200/50">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold text-gray-700">Journal</th>
                    <th className="text-left px-6 py-4 font-semibold text-gray-700">File</th>
                    <th className="text-center px-6 py-4 font-semibold text-gray-700">Score</th>
                    <th className="text-center px-6 py-4 font-semibold text-gray-700">Status</th>
                    <th className="text-right px-6 py-4 font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAnalyses.map((analysis, idx) => (
                    <tr
                      key={analysis.id}
                      onClick={() => navigate(`/qc/analysis/${analysis.id}`)}
                      className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-blue-50/50 transition-colors duration-300 cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 rounded-lg font-medium text-sm">
                          {analysis.journalCode}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium">{analysis.uploadedFilename}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="relative w-20 h-20 flex items-center justify-center">
                            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 120 120">
                              <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="#e5e7eb"
                                strokeWidth="8"
                              />
                              <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke={`url(#scoreGradient-${idx})`}
                                strokeWidth="8"
                                strokeDasharray={`${(analysis.overallScore || 0) * 3.4} 340`}
                                strokeLinecap="round"
                                className="transition-all duration-500"
                              />
                              <defs>
                                <linearGradient id={`scoreGradient-${idx}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                  {(analysis.overallScore || 0) >= 80 ? (
                                    <>
                                      <stop offset="0%" stopColor="#10b981" />
                                      <stop offset="100%" stopColor="#14b8a6" />
                                    </>
                                  ) : (analysis.overallScore || 0) >= 60 ? (
                                    <>
                                      <stop offset="0%" stopColor="#f59e0b" />
                                      <stop offset="100%" stopColor="#f97316" />
                                    </>
                                  ) : (
                                    <>
                                      <stop offset="0%" stopColor="#ef4444" />
                                      <stop offset="100%" stopColor="#ec4899" />
                                    </>
                                  )}
                                </linearGradient>
                              </defs>
                            </svg>
                            <span className="absolute text-sm font-bold text-gray-900">{Math.round(analysis.overallScore || 0)}%</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg font-medium text-sm transition-all duration-300 ${
                          analysis.overallStatus === 'PASSED'
                            ? 'bg-green-100/80 text-green-700 group-hover:bg-green-100'
                            : analysis.overallStatus === 'NEEDS_REVIEW'
                            ? 'bg-yellow-100/80 text-yellow-700 group-hover:bg-yellow-100'
                            : 'bg-red-100/80 text-red-700 group-hover:bg-red-100'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${
                            analysis.overallStatus === 'PASSED'
                              ? 'bg-green-600 animate-pulse'
                              : analysis.overallStatus === 'NEEDS_REVIEW'
                              ? 'bg-yellow-600 animate-pulse'
                              : 'bg-red-600 animate-pulse'
                          }`} />
                          {analysis.overallStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-gray-600 text-sm">{formatDate(analysis.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QCDashboard;
